import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { ClientPoliciesActions } from '@lockton-apps/clients/actions';
import {
  DashboardAdminActions,
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardBrokerNorwayActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import { EnquiryOverviewActions } from '@lockton-apps/enquiries/action';
import {
  PolicyCreateUpdateActions,
  PolicyDetailsActions,
} from '@lockton-apps/policies/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { PoliciesApiActions } from '@lockton-apps/shared/action/policies';
import { Policy } from '@lockton-apps/shared/interfaces';
import { PoliciesStateActions } from './policies.actions';

export const POLICIES_FEATURE_KEY = 'policies';

export interface PoliciesState extends EntityState<Policy> {
  selectedId?: string | number | null; // which Policies record has been selected
  loaded: boolean; // has the Policies list been loaded
  updating: boolean;
  filter: Record<string, unknown>;
  error?: unknown; // last known error (if any) #TODO
}

export interface PoliciesPartialState {
  readonly [POLICIES_FEATURE_KEY]: PoliciesState;
}

export const policiesAdapter: EntityAdapter<Policy> =
  createEntityAdapter<Policy>();

export const initialPoliciesState: PoliciesState =
  policiesAdapter.getInitialState({
    // set initial required properties
    loaded: false,
    updating: false,
    filter: {
      policyType: '',
      status: '',
      fromDate: null,
      toDate: null,
    },
  } as unknown as PoliciesState);

const reducer = createReducer(
  initialPoliciesState,

  /* Client Policies View */
  on(
    ClientPoliciesActions.init,
    DashboardBrokerLsosActions.init,
    DashboardBrokerNorwayActions.init,
    DashboardBrokerActions.init,
    PolicyDetailsActions.init,
    DashboardContactActions.init,
    EnquiryOverviewActions.init,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    }),
  ),
  on(
    PoliciesApiActions.loadPoliciesSuccess,
    PoliciesApiActions.loadPoliceiesForContactDashboardSuccess,
    (state, { policies }) =>
      policiesAdapter.setAll(policies, { ...state, loaded: true }),
  ),
  on(
    PoliciesApiActions.loadClientPoliciesSuccess,
    PoliciesApiActions.loadClientsPoliciesSuccess,
    PoliciesApiActions.loadSeveralSuccess,
    (state, { policies }) =>
      policiesAdapter.upsertMany(policies, { ...state, loaded: true }),
  ),
  on(
    PoliciesApiActions.loadPoliciesFailure,
    PoliciesApiActions.loadClientPoliciesFailure,
    (state, { error }) => ({
      ...state,
      error,
    }),
  ),

  on(
    PoliciesApiActions.loadPolicySuccess,
    PoliciesApiActions.loadPolicyForPolicyDetailsSuccess,
    PoliciesApiActions.loadPolicySuccessThenLoadClientBrokers,
    (state, { policy }) =>
      policiesAdapter.upsertOne(policy, {
        ...state,
        loaded: true,
      }),
  ),
  on(PoliciesApiActions.loadPolicyFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Load Recently Viewed
  on(DashboardAdminActions.init, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(
    PoliciesApiActions.loadRecentlyViewedPoliciesSuccess,
    (state, { policies }) =>
      policiesAdapter.setAll(policies, { ...state, loaded: true }),
  ),
  on(
    PoliciesApiActions.loadRecentlyViewedPoliciesFailure,
    (state, { error }) => ({
      ...state,
      error,
    }),
  ),

  // Load all
  on(AuthApiActions.contactAuthenticated, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),

  // add
  on(
    PolicyCreateUpdateActions.added,
    PolicyCreateUpdateActions.updated,
    (state) => ({
      ...state,
      updating: true,
    }),
  ),
  on(PoliciesApiActions.addedSuccess, (state, { policy }) =>
    policiesAdapter.addOne(policy, {
      ...state,
      updating: false,
    }),
  ),
  on(PoliciesApiActions.updatedSuccess, (state, { policy }) =>
    policiesAdapter.upsertOne(policy, {
      ...state,
      updating: false,
    }),
  ),
  on(
    PoliciesApiActions.addedFailure,
    PoliciesApiActions.updatedFailure,
    (state, { error }) => ({
      ...state,
      updating: false,
      error,
    }),
  ),
  on(PoliciesStateActions.deleteConfirmation, (state, { id }) =>
    policiesAdapter.mapOne(
      { id, map: (policy) => ({ ...policy, isDeleted: true }) },
      { ...state, updating: true },
    ),
  ),
  on(PoliciesApiActions.deletedFailure, (state, { id }) =>
    policiesAdapter.mapOne(
      { id, map: (policy) => ({ ...policy, isDeleted: false }) },
      { ...state, updating: false },
    ),
  ),
  on(PolicyDetailsActions.restored, (state, { id }) =>
    policiesAdapter.mapOne(
      { id, map: (policy) => ({ ...policy, isDeleted: false }) },
      { ...state, updating: true },
    ),
  ),
  on(PoliciesApiActions.restoredFailure, (state, { id }) =>
    policiesAdapter.mapOne(
      { id, map: (policy) => ({ ...policy, isDeleted: true }) },
      { ...state, updating: false },
    ),
  ),
  on(PoliciesApiActions.deletedSuccess, (state) => ({
    ...state,
    updating: false,
  })),
);

export function policiesReducer(
  state: PoliciesState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
